import { ITask, ITaskUpdate, TTask, TTaskUpdate } from "@propely/propely-typings";
import { ToastUtilities } from "@src/libs/react-toastify/ToastUtilities";
import { getAuth } from "firebase/auth";
import { Timestamp, collection, doc, getFirestore, updateDoc } from "firebase/firestore";
import { t } from "i18next";
import { QueryClient, UseQueryResult, useMutation, useQuery, useQueryClient } from "react-query";
import { propelyApi } from "@src/libs/firebase/api";
import { z } from "zod";
import { useMemo } from "react";
import { useRealtimeCollectionData } from "../generic/useRealtimeCollectionData";

interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}
export type TasksEndpointFilters = {
  propertyID?: string;
  repetitiveTasks?: boolean;
  archived?: boolean;
  status?: number[];
  priority?: number[];
  executors?: string[];
  responsibles?: string[];
  taskTypeID?: string[];
  tags?: string[];
  dateRange?: DateRange;
};

//Key
//The keys are mapped out in the filter to ensure consistent key structure, object properties order can differ
export const makeTaskKey = (filters?: TasksEndpointFilters) => [
  "tasks",
  {
    propertyID: filters?.propertyID,
    repetitiveTasks: filters?.repetitiveTasks,
    archived: filters?.archived,
    status: filters?.status,
    priority: filters?.priority,
    executors: filters?.executors,
    responsibles: filters?.responsibles,
    taskTypeID: filters?.taskTypeID,
    tags: filters?.tags,
    dateRange: filters?.dateRange
      ? {
          startDate: filters.dateRange.startDate?.toISOString(),
          endDate: filters.dateRange.endDate?.toISOString(),
        }
      : undefined,
  },
];

//Queries
//This query returns more metadata than the useTasks query
export function useTaskById(id: string): UseQueryResult<ITask> {
  const params = new URLSearchParams();
  params.append("formatToITask", "true");
  const taskQuery = useQuery(["task", { taskId: id }], async () => {
    const response = await propelyApi().get(`/tasks/${id}?${params.toString()}`);
    return TTask.parse(response.data.data);
  });
  return taskQuery;
}

export const useTasks = (
  { propertyID, repetitiveTasks, archived, status, priority, executors, responsibles, taskTypeID, tags, dateRange }: TasksEndpointFilters,
  options?: { enabled?: boolean; refetchOnMount?: boolean }
) => {
  // URL starts with /tasks and then logic to add query parameters
  const params = new URLSearchParams();
  params.append("formatToITask", "true");
  if (propertyID !== undefined) params.append("propertyID", propertyID);
  if (repetitiveTasks !== undefined) params.append("repetitiveTasks", repetitiveTasks.toString());
  if (archived !== undefined) params.append("archived", archived.toString());
  if (status !== undefined) params.append("status", status.toString());
  if (priority !== undefined) params.append("priority", priority.toString());
  if (executors !== undefined) params.append("executors", executors.toString());
  if (responsibles !== undefined) params.append("responsibles", responsibles.toString());
  if (taskTypeID !== undefined) params.append("taskTypeID", taskTypeID.toString());
  if (tags !== undefined) params.append("tags", tags.toString());
  if (dateRange?.startDate) {
    params.append("startDate", dateRange.startDate.toString());
  }
  if (dateRange?.endDate) {
    params.append("endDate", dateRange.endDate.toString());
  }

  const key = makeTaskKey({ propertyID, repetitiveTasks, archived, status, priority, executors, responsibles, taskTypeID, tags, dateRange });
  return useQuery(
    key,
    (): Promise<ITask[]> =>
      propelyApi()
        .get(`/tasks?${params.toString()}`)
        .then((response) => {
          const successfulParsedTasks: ITask[] = response.data.datas
            .map((task: ITask): z.SafeParseSuccess<ITask> | z.SafeParseError<ITask> => {
              const safeParsedTask = TTask.safeParse(task);
              if (!safeParsedTask.success) {
                console.warn("Task failed to parse!", "\n Task:", task, "\n Error object:", safeParsedTask);
              }
              return safeParsedTask;
            })
            .filter((task: z.SafeParseSuccess<ITask> | z.SafeParseError<ITask>) => task.success)
            .map((task: z.SafeParseSuccess<ITask>) => task.data);

          return successfulParsedTasks;
        }),
    options && { enabled: options.enabled, refetchOnMount: options.refetchOnMount }
  );
};

//Mutations
/**
 * A custom hook for updating tasks.
 *
 * @param onSuccessCallback - An optional callback function that will be executed
 * after the mutation completes successfully. Use this to perform additional actions
 * such as showing a success message or refreshing related data.
 */
export const useUpdateTasks = (onSuccessCallback?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      tasks,
      update,
      comment,
      visibleForTenants,
      filesToUpload,
      filesToRemove,
    }: {
      tasks: ITask[];
      update: Partial<ITask>;
      comment?: string | null;
      visibleForTenants?: boolean | null;
      filesToUpload?: { files: { name: string; file: string; taskPaths: string[]; propertyIDs: string[] }[] } | null;
      filesToRemove?: { fileId: string; taskPath: string } | null;
    }) => {
      const singular: boolean = tasks.length === 1;

      return ToastUtilities.presentPromise(
        t("main.taskUpdating"),
        Promise.all(
          tasks.map((task) =>
            propelyApi().post(`/properties/${task.propertyID}/tasks/${task.id}/update`, {
              update: update ?? {},
              visibleForTenants,
              comment,
              filesToUpload: filesToUpload ?? {},
              filesToRemove: filesToRemove ?? {},
            })
          )
        ),
        singular ? t("main.taskUpdated") : t("main.tasksUpdated"),
        singular ? t("error.updatingTask") : t("error.updatingTasks")
      );
    },
    {
      onMutate({ tasks, update, comment, visibleForTenants, filesToUpload, filesToRemove }) {
        const singular: boolean = tasks.length === 1;
        let previousQueries: any[] = [];
        const taskQueries = queryClient.getQueryCache().findAll("task");
        const tasksQueries = queryClient.getQueryCache().findAll("tasks");
        const allTaskQueries = taskQueries.concat(tasksQueries);
        allTaskQueries.forEach((query) => {
          queryClient.cancelQueries(query.queryKey);
          previousQueries.push(queryClient.getQueryData(query.queryKey));
          queryClient.setQueryData(query.queryKey, (oldData: ITask | ITask[] | undefined) => {
            if (!oldData) return [];
            if (!Array.isArray(oldData)) {
              // Handle single task case (from useTaskById)
              return tasks.some((task) => task.id === oldData.id) ? { ...oldData, ...update } : oldData;
            }
            // Handle task array case (from useTasks)
            return oldData.map((t: ITask) => (tasks.some((task) => task.id === t.id) ? { ...t, ...update } : t));
          });
        });
        return { previousQueries, singular, update };
      },
      onError(err, variables, context) {
        context?.previousQueries.forEach((query) => {
          queryClient.setQueryData(query.key, query.data);
        });
      },
      onSuccess(data, variables, context) {
        invalidateAllTaskQueries(queryClient);

        // Call the success callback if provided
        onSuccessCallback?.();
      },
    }
  );
};

export const useDeleteTasks = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (tasks: ITask[]) => {
      //TODO: change to API
      return Promise.all(
        tasks.map((task) =>
          updateDoc(doc(getFirestore(), `/properties/${task.propertyID}/deviations`, task.id), {
            deletedBy: {
              userID: getAuth().currentUser?.uid,
              date: Timestamp.fromDate(new Date()),
            },
          })
        )
      );
    },
    {
      onMutate: async (tasks) => {
        const taskQueries = queryClient.getQueryCache().findAll("task");
        const tasksQueries = queryClient.getQueryCache().findAll("tasks");
        const allTaskQueries = taskQueries.concat(tasksQueries);
        let previousQueries: any[] = [];
        allTaskQueries.forEach((query) => {
          queryClient.cancelQueries(query.queryKey);
          previousQueries.push(queryClient.getQueryData(query.queryKey));
          queryClient.setQueryData(query.queryKey, (oldData: ITask[] | undefined): ITask[] => {
            if (!oldData || !Array.isArray(oldData)) return [];
            return oldData.filter((t: ITask) => !tasks.some((task) => task.id === t.id));
          });
        });
        return { previousQueries };
      },
      onError: (err, variables, context) => {
        ToastUtilities.presentError(t("error.deletingTasks"));
        context?.previousQueries.forEach((query) => {
          queryClient.setQueryData(query.key, query.data);
        });
      },
      onSuccess: (data, variables, context) => {
        invalidateAllTaskQueries(queryClient);
        ToastUtilities.presentSuccess(t("main.tasksDeleted"));
      },
    }
  );
};

//TODO change name to something with realtime listener
export const useTaskUpdates = (propertyID: string, taskID: string) => {
  const query = useMemo(() => collection(getFirestore(), `/properties/${propertyID}/deviations/${taskID}/updates`), [propertyID, taskID]);
  return useRealtimeCollectionData<ITaskUpdate>(query, TTaskUpdate);
};

export function invalidateAllTaskQueries(queryClient: QueryClient) {
  const taskQueries = queryClient.getQueryCache().findAll("task");
  const tasksQueries = queryClient.getQueryCache().findAll("tasks");
  const allTaskQueries = taskQueries.concat(tasksQueries);
  allTaskQueries.forEach((query) => {
    queryClient.invalidateQueries(query.queryKey);
  });
}

/**
 * @deprecated use functions from task.ts instead
 */
export const addSeenByToTask = async (propertyID: string, taskID: string, userID: string) => {
  const reference = doc(getFirestore(), "properties", propertyID, "deviations", taskID);
  await updateDoc(reference, {
    ["seenBy." + userID]: { date: new Date() },
  });
};

export interface SendToFileInboxInput {
  propertyID: string;
  taskID: string;
  fileID: string;
  subCollectionID: string;
  subCollection: "updates" | "tasks";
}

const sendTaskFileToInbox = async (input: SendToFileInboxInput) => {
  const url = `/properties/${input.propertyID}/tasks/${input.taskID}/sendToInbox`;
  const promise = propelyApi().post(url, { subCollection: input.subCollection, subCollectionID: input.subCollectionID, fileID: input.fileID });
  ToastUtilities.presentSavingToast(promise);
};

export const useSendTaskFileToInbox = () => useMutation(sendTaskFileToInbox);
